import { QueryDocumentSnapshot } from 'firebase/firestore';
import { CharacterCard, CardState } from '../types';
import { convertFirestoreExifData } from './dataUtils';
import { logger } from './secureLogger';
import { isSupportedCardType } from './supportedCardTypes';
import { parseGenerationMetadata } from './generationMetadata';
import { parseDescriptionMeta } from './descriptionMeta';

/**
 * Firestoreドキュメントから統一されたCharacterCardオブジェクトを生成
 */
export const processFirestoreCard = (
  doc: QueryDocumentSnapshot, 
  options: {
    includePrivate?: boolean;
    currentUserId?: string;
  } = {}
): CharacterCard | null => {
  try {
    const data = doc.data();

    if (!isSupportedCardType(data.characterData?.cardType)) {
      return null;
    }

    // 日付の統一変換処理
    const createdAt = convertFirestoreTimestamp(data.createdAt);
    const updatedAt = convertFirestoreTimestamp(data.updatedAt);
    
    // EXIFデータの統一変換処理
    const exifData = convertFirestoreExifData(data.exifData);
    
    // 統一されたカードオブジェクトを作成
    return {
      id: doc.id,
      userId: data.userId || '',
      originalImageUrl: undefined, // セキュリティとパフォーマンスのため削除
      characterData: data.characterData,
      exifData,
      pixelArtUrl: data.pixelArtUrl,
      status: CardState.SUCCESS, // DBに保存されているカードは常にSUCCESS
      error: data.error,
      createdAt,
      updatedAt,
      // 既存カードには存在しない → undefined のまま（後方互換）
      generationMetadata: parseGenerationMetadata(data.generationMetadata),
      descriptionMeta: parseDescriptionMeta(data.descriptionMeta),
    };
  } catch (error) {
    logger.error('Error processing card document:', error);
    return null;
  }
};

/**
 * Firestoreタイムスタンプを統一的にDate型に変換
 */
export const convertFirestoreTimestamp = (timestamp: any): Date => {
  if (!timestamp) {
    return new Date();
  }
  
  // Firestoreのタイムスタンプオブジェクト
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // 既にDateオブジェクト
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // 文字列やその他の形式
  try {
    return new Date(timestamp);
  } catch (error) {
    logger.warn('Invalid timestamp format, using current date:', timestamp);
    return new Date();
  }
};

/**
 * カードの配列から重複を除去（IDベース）
 */
export const deduplicateCards = (cards: CharacterCard[]): CharacterCard[] => {
  const seen = new Set<string>();
  return cards.filter(card => {
    if (seen.has(card.id)) {
      return false;
    }
    seen.add(card.id);
    return true;
  });
};

/**
 * カードの配列を日付順でソート（新しい順）
 */
export const sortCardsByDate = (cards: CharacterCard[]): CharacterCard[] => {
  return [...cards].sort((a, b) => {
    const dateA = a.createdAt || new Date(0);
    const dateB = b.createdAt || new Date(0);
    return dateB.getTime() - dateA.getTime();
  });
};

/**
 * 複数のFirestoreドキュメントを効率的に処理
 */
export const processFirestoreCards = (
  docs: QueryDocumentSnapshot[], 
  options: {
    includePrivate?: boolean;
    currentUserId?: string;
    deduplicate?: boolean;
    sort?: boolean;
  } = {}
): CharacterCard[] => {
  const { deduplicate = false, sort = false } = options;
  
  // 各ドキュメントを処理
  let cards = docs
    .map(doc => processFirestoreCard(doc, options))
    .filter((card): card is CharacterCard => card !== null);
  
  // 重複除去
  if (deduplicate) {
    cards = deduplicateCards(cards);
  }
  
  // ソート
  if (sort) {
    cards = sortCardsByDate(cards);
  }
  
  return cards;
};

/**
 * カードデータのバリデーション
 */
export const validateCard = (card: CharacterCard): boolean => {
  if (!card.id || !card.userId) {
    return false;
  }
  
  if (!card.pixelArtUrl && card.status === CardState.SUCCESS) {
    return false;
  }
  
  return true;
};

/**
 * フィード表示用の軽量カード処理（EXIF処理を最小化）
 */
export const processFirestoreCardLight = (
  doc: QueryDocumentSnapshot, 
  options: {
    includePrivate?: boolean;
    currentUserId?: string;
  } = {}
): CharacterCard | null => {
  try {
    const data = doc.data();

    if (!isSupportedCardType(data.characterData?.cardType)) {
      return null;
    }

    // 軽量な日付変換（FirestoreタイムスタンプのtoDate()のみ使用）
    const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
    const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date();
    
    // EXIF処理をスキップ（フィード表示では不要）
    
    return {
      id: doc.id,
      userId: data.userId || '',
      originalImageUrl: undefined,
      characterData: data.characterData,
      exifData: data.exifData ? convertFirestoreExifData(data.exifData) : null, // EXIF日付を表示用に保持
      pixelArtUrl: data.pixelArtUrl,
      status: CardState.SUCCESS,
      error: data.error,
      createdAt,
      updatedAt,
      // 既存カードには存在しない → undefined のまま（後方互換）
      generationMetadata: parseGenerationMetadata(data.generationMetadata),
      descriptionMeta: parseDescriptionMeta(data.descriptionMeta),
    };
  } catch (error) {
    logger.error('Error processing card document (light):', error);
    return null;
  }
};

/**
 * フィード表示用の軽量カード配列処理
 */
export const processFirestoreCardsLight = (
  docs: QueryDocumentSnapshot[], 
  options: {
    includePrivate?: boolean;
    currentUserId?: string;
  } = {}
): CharacterCard[] => {
  return docs
    .map(doc => processFirestoreCardLight(doc, options))
    .filter((card): card is CharacterCard => card !== null);
};

/**
 * パフォーマンス測定用のユーティリティ
 */
export const measureCardProcessing = <T>(
  operation: () => T,
  operationName: string
): T => {
  const start = performance.now();
  const result = operation();
  const end = performance.now();
  
  logger.debug(`Card processing: ${operationName} took ${(end - start).toFixed(2)}ms`);
  return result;
};
